import React from "react";
import {
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Phone,
  Mail,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { motion } from "framer-motion";

const Footer = () => {
  return (
    <footer className="bg-primary text-white py-20 overflow-hidden relative">
      <div className="absolute top-0 left-0 w-full h-1 bg-accent" />

      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 md:gap-8 lg:gap-12 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="md:col-span-1 space-y-8"
          >
            <a
              href="/"
              className="inline-block"
              aria-label="Página inicial da Pinheiro Advocacia"
            >
              <Logo variant="light" className="h-16 w-auto" />
            </a>
            <p className="text-white/60 leading-relaxed text-base pr-0 lg:mr-[-30px]">
              Atuação estratégica e dedicada exclusivamente à proteção da
              remuneração e aposentadoria dos servidores públicos.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/advocaciavaleriapinheiro/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 p-3 rounded-full hover:bg-accent hover:text-white transition-all"
                aria-label="Facebook"
              >
                <Facebook size={24} />
              </a>
              <a
                href="https://www.instagram.com/pinheiro_advocacia_/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 p-3 rounded-full hover:bg-accent hover:text-white transition-all"
                aria-label="Instagram"
              >
                <Instagram size={24} />
              </a>
              <a
                href="https://www.linkedin.com/company/76507699/admin/page-posts/published/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 p-3 rounded-full hover:bg-accent hover:text-white transition-all"
                aria-label="LinkedIn"
              >
                <Linkedin size={24} />
              </a>
              <a
                href="https://www.youtube.com/@pinheiro.advocacia"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 p-3 rounded-full hover:bg-accent hover:text-white transition-all"
                aria-label="YouTube"
              >
                <Youtube size={24} />
              </a>
              <a
                href="https://www.tiktok.com/@pinheiro_advocacia_?_r=1&_t=ZS-94MvFhcn7DE"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 p-3 rounded-full hover:bg-accent hover:text-white transition-all"
                aria-label="TikTok"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
                </svg>
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="space-y-8 lg:pl-12 xl:pl-16"
          >
            <h4 className="text-xl font-extrabold border-b-2 border-accent pb-4 inline-block">
              Links Rápidos
            </h4>
            <ul className="space-y-4 text-white/70 font-bold">
              <li>
                <a
                  href="/"
                  className="hover:text-accent transition-colors text-border"
                >
                  Início
                </a>
              </li>
              <li>
                <a
                  href="/sobre"
                  className="hover:text-accent transition-colors text-border"
                >
                  Sobre
                </a>
              </li>
              <li>
                <a
                  href="/avaliacoes"
                  className="hover:text-accent transition-colors text-border"
                >
                  Avaliações
                </a>
              </li>
              <li>
                <a
                  href="/blog"
                  className="hover:text-accent transition-colors text-border"
                >
                  Blog
                </a>
              </li>
              <li>
                <a
                  href="/redes-sociais"
                  className="hover:text-accent transition-colors text-border"
                >
                  Redes Sociais
                </a>
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-8 lg:pl-8 xl:pl-12"
          >
            <h4 className="text-xl font-extrabold border-b-2 border-accent pb-4 inline-block">
              Áreas de Atuação
            </h4>
            <ul className="space-y-4 text-white/70 font-bold">
              <li>
                <a
                  href="/areas-de-atuacao/professores-quinquenios"
                  className="hover:text-accent transition-colors text-border"
                >
                  Professores
                </a>
              </li>
              <li>
                <a
                  href="/areas-de-atuacao/aposentados-e-pensionistas"
                  className="hover:text-accent transition-colors text-border"
                >
                  Aposentados e Pensionistas
                </a>
              </li>
              <li>
                <a
                  href="/areas-de-atuacao/imposto-de-renda"
                  className="hover:text-accent transition-colors text-border"
                >
                  Imposto de Renda
                </a>
              </li>
              <li>
                <a
                  href="/areas-de-atuacao/servidores-da-saude"
                  className="hover:text-accent transition-colors text-border"
                >
                  Servidores da Saúde
                </a>
              </li>
              <li>
                <a
                  href="/areas-de-atuacao/policiais"
                  className="hover:text-accent transition-colors text-border"
                >
                  Policiais
                </a>
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="md:col-span-2 lg:col-span-1 space-y-8 lg:pl-8 xl:pl-12"
          >
            <h4 className="text-xl font-extrabold border-b-2 border-accent pb-4 inline-block">
              Onde Estamos
            </h4>
            <div className="space-y-4 text-white/70 text-lg">
              <p className="font-bold text-white">Sede - Sorocaba/SP</p>
              <p className="text-base text-border pr-0 lg:pr-4">
                Rua Antonio Ferreira, 24 - Campolim - Sorocaba/SP - CEP
                18.047.636
              </p>
              <div className="space-y-2 pt-4">
                <a
                  href="https://api.whatsapp.com/send?phone=5515997606362&text=Ol%C3%A1%2C%20eu%20quero%20falar%20com%20um%20advogado"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-base text-border hover:text-accent transition-colors"
                >
                  <Phone size={18} />
                  <span>(15) 99760-6362</span>
                </a>
                <a
                  href="mailto:atendimento@advpinheiro.com.br"
                  className="flex items-center gap-2 text-base text-border hover:text-accent transition-colors"
                >
                  <Mail size={22} />
                  <span>atendimento@advpinheiro.com.br</span>
                </a>
              </div>
              <div className="pt-4 space-y-2 text-sm text-white/40">
                <p className="text-border">CNPJ: 42.833.344/0001-21</p>
                <p className="text-border">OAB/SP: 38.584</p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-6 text-white/70 text-sm px-0 md:px-[120px]">
          <p className="text-center">
            © {new Date().getFullYear()} Pinheiro Advocacia. Todos os Direitos
            Reservados.
          </p>
          <div className="flex flex-wrap justify-center gap-8">
            <a
              href="/politica-de-privacidade"
              className="hover:text-white transition-colors"
            >
              Política de Privacidade
            </a>
            <p>
              Desenvolvido por{" "}
              <a
                href="https://aquinoagency.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white font-bold hover:text-accent transition-colors"
              >
                Aquino Agency
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
